import * as stylex from '@stylexjs/stylex'
import { type MouseEvent, type ElementType, useRef, useCallback } from 'react'
import { Box, type BoxProps } from '../Box/Box'
import { Ripple, type RippleProps, type RippleHandle } from '../Ripple/Ripple'
import { typography } from '../tokens/typography.stylex'

const baseStyles = stylex.create({
  root: {
    WebkitTapHighlightColor: 'transparent',
    font: 'inherit',
    margin: 0,
    padding: 0,
    borderRadius: 0,
    borderStyle: 'none',
    borderWidth: 0,
    outline: 'none',
    textDecoration: 'none',
    alignItems: 'center',
    appearance: 'none',
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
    color: 'inherit',
    cursor: {
      'default': 'pointer',
      ':disabled': 'not-allowed',
    },
    display: 'inline-flex',
    fontFamily: typography.font,
    fontWeight: 500,
    justifyContent: 'center',
    lineHeight: 'inherit',
    opacity: 1,
    position: 'relative',
    /**
     * Below styles are fixed for flatsome
     */
    textTransform: 'none',
    userSelect: 'none',
    verticalAlign: 'middle',
    minHeight: 'initial',
  },
})

export type ButtonBaseProps<RootComponent extends ElementType = 'button', BoxElement extends HTMLElement = HTMLButtonElement> = BoxProps<RootComponent, BoxElement> & {
  /**
   * 是否停用水波紋效果
   * @default false
   */
  disableRipple?: boolean
  /** 傳遞給內部 Ripple 元件的額外 props */
  RippleProps?: Omit<RippleProps, 'rootRef'>
}

/**
 * ButtonBase 是所有按鈕元件的底層基礎，提供無樣式的可點擊區域與水波紋效果。
 * 一般不直接使用，請改用 Button 或 IconButton。
 */
export const ButtonBase = <RootComponent extends ElementType = 'button', BoxElement extends HTMLElement = HTMLButtonElement>(props: ButtonBaseProps<RootComponent, BoxElement>) => {
  const {
    component = 'button',
    rootStyle,
    children,
    onPointerDown,
    onPointerUp,
    onPointerLeave,
    disableRipple: disableRippleProp = false,
    disabled,
    RippleProps,
    ...rest
  } = props
  const rippleRef = useRef<RippleHandle>(null)
  const disableRipple = disableRippleProp || disabled

  const handlePointerDown = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    onPointerDown?.(event)
    rippleRef.current?.startRipple(event)
  }, [onPointerDown])

  const handlePointerUp = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    onPointerUp?.(event)
    rippleRef.current?.stopRipple()
  }, [onPointerUp])

  const handlePointerLeave = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    onPointerLeave?.(event)
    rippleRef.current?.stopRipple()
  }, [onPointerLeave])

  return (
    <Box
      component={component}
      type="button"
      rootStyle={[baseStyles.root, rootStyle]}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      disabled={disabled}
      {...rest}
    >
      {!disableRipple && <Ripple rootRef={rippleRef} {...RippleProps} />}
      {children}
    </Box>
  )
}
